'use client'

/**
 * Can this verb be done on these copies right now, and if not, WHY (owner, 27 Oct 2026: "if the permission is not
 * there, we can say that it can't be done, as permission is not there. But don't remove it.").
 *
 * Every verb stays in the menus. Trash needs only the remediation grant (the PermissionButton / PermissionMenuItem
 * around it says so). A permanent Delete also needs, in the order a person can act on them:
 *   role            delete:ErHardDelete
 *   switch          the workspace's hard-delete switch (GET /settings/delete-gate)
 *   provider_scope  a provider that grants it: Google has not (https://mail.google.com/ is not requested), so a
 *                   selection of Google copies only, or a Google row, says so and names the domain.
 * The answer is a DisabledWithReason sentence, in the interface language. A selection that MIXES providers is not
 * refused here: the preview answers with the Google count and the page offers the Microsoft copies or Trash.
 * While the gate is loading nothing is refused (the API checks every one anyway).
 */
import * as React from 'react'
import { usePermissionGate } from '@/components/auth/permission-gate'
import { useTranslation } from '@/lib/i18n'
import { useSnrDeleteGate } from '@/lib/er/hooks/use-search-remediate-v2'
import type { SnrActionVerb, SnrProvider } from '@/lib/er/snr-query/types'
import { SNR_DELETE_GATE_DOMAIN_KEY, SNR_DELETE_GATE_KEYS } from './snr-labels'
import { SNR_HARD_DELETE } from './snr-permissions'

export interface SnrVerbGateState {
  disabled: boolean
  /** The sentence DisabledWithReason shows; null when the verb can be done. */
  reason: string | null
}

/** What the page knows about the copies a verb would act on. */
export interface SnrVerbTarget {
  /** The providers of the copies, when known (a row, the ticked rows, the facets of "all matching"). */
  providers?: ReadonlyArray<SnrProvider>
  /** The domain's name, for "Google has not granted permanent delete for <domain>". */
  domainName?: string | null
}

const OPEN: SnrVerbGateState = { disabled: false, reason: null }

/** Hands a function: the gate of a verb for a target. One hook call per component; the function is cheap. */
export function useSnrVerbGate(): (verb: SnrActionVerb, target?: SnrVerbTarget) => SnrVerbGateState {
  const { t } = useTranslation()
  const role = usePermissionGate(SNR_HARD_DELETE.action, SNR_HARD_DELETE.subject)
  const state = useSnrDeleteGate().data
  const roleDenied = role.denied
  return React.useCallback(
    (verb: SnrActionVerb, target: SnrVerbTarget = {}): SnrVerbGateState => {
      if (verb !== 'Delete') return OPEN
      if (roleDenied) return { disabled: true, reason: t(SNR_DELETE_GATE_KEYS.role) }
      if (!state) return OPEN
      if (!state.hardDeleteEnabled) return { disabled: true, reason: t(SNR_DELETE_GATE_KEYS.switch) }
      const providers = target.providers ?? []
      const onlyGoogle = providers.length > 0 && providers.every((p) => p === 'google')
      if (onlyGoogle && !state.providers.google) {
        return {
          disabled: true,
          reason: target.domainName
            ? t(SNR_DELETE_GATE_DOMAIN_KEY, { domain: target.domainName })
            : t(SNR_DELETE_GATE_KEYS.provider_scope),
        }
      }
      return OPEN
    },
    [roleDenied, state, t],
  )
}
